import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocale, useLocalePath, useTranslation } from "@thyra/landing-i18n";
import { useLatestPosts } from "../lib/blog-queries";
import { PostCard } from "./blog/PostCard";
import { SectionHeader } from "./ui/SectionHeader";

/**
 * Three newest blog posts. Renders nothing until there is at least one post,
 * so an unconfigured or empty CMS leaves no trace on the home page. Data is
 * seeded from the prerender and refreshed in the browser.
 */
export function LatestPostsSection() {
  const { t } = useTranslation("sections");
  const locale = useLocale();
  const lp = useLocalePath();
  // The page's own language, else the English posts (cards are marked lang="en").
  const { data } = useLatestPosts(locale);
  if (!data || data.length === 0) return null;

  return (
    <section className="w-full py-16 sm:py-24" aria-labelledby="latest-posts">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t("latestPosts.eyebrow")}
          title={<span id="latest-posts">{t("latestPosts.title")}</span>}
          description={t("latestPosts.description")}
        />
        <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((post) => (
            <li key={post.id}>
              <PostCard post={post} headingLevel={3} />
            </li>
          ))}
        </ul>
        <div className="mt-10 flex justify-center">
          <Link
            to={lp("/blog")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            {t("latestPosts.viewAll")}
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
